import { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Dialog } from '@/components/ui/Dialog';
import { KnowledgeLink } from '@/components/ui/KnowledgeLink';

/**
 * A button with an info icon that opens a dialog explaining P&L (Profit & Loss).
 * Reusable across list view, analytics view, and other places showing P&L values.
 */
export function PnlInfoButton() {
    const [open, setOpen] = useState(false);

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="p-0.5 text-gray-400 hover:text-accent transition-colors"
                title="About P&L"
            >
                <InformationCircleIcon className="h-4 w-4" />
            </button>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                title="About P&L"
                size="sm"
            >
                <div className="space-y-4 text-sm text-gray-600 dark:text-gray-300">
                    <p>
                        <strong className="text-gray-900 dark:text-white">P&L (Profit & Loss)</strong> shows your total
                        gain or loss across all assets.
                    </p>
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">Period:</p>
                        <p className="text-gray-500 dark:text-gray-400">
                            From your first deposit to today, for each asset.
                        </p>
                    </div>
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">How it's calculated:</p>
                        <p className="text-gray-500 dark:text-gray-400">
                            Current Value + Withdrawals - Deposits
                        </p>
                    </div>
                    <div>
                        <p className="font-medium text-gray-900 dark:text-white mb-1">Example:</p>
                        <ul className="list-disc list-inside space-y-0.5 text-gray-500 dark:text-gray-400">
                            <li>Deposited: €10,000</li>
                            <li>Withdrew: €2,000</li>
                            <li>Current value: €12,000</li>
                            <li>P&L: €4,000</li>
                        </ul>
                    </div>
                    <p className="text-gray-500 dark:text-gray-400">
                        This measures how much your investments have grown relative to what you put in,
                        accounting for any withdrawals.
                    </p>
                    <p className="text-gray-500 dark:text-gray-400">
                        <strong className="text-gray-700 dark:text-gray-200">Note:</strong> Archived assets are always included in
                        P&L calculations, even when the archived filter is hidden.
                    </p>
                    <p className="pt-2 border-t border-gray-200 dark:border-gray-700">
                        <KnowledgeLink page="pnl">Learn more about P&L</KnowledgeLink>
                    </p>
                </div>
            </Dialog>
        </>
    );
}
